import React from 'react';
import { defineMessages } from 'react-intl';
import { DeleteOutlined, SaveOutlined, StopOutlined, UserOutlined } from '@ant-design/icons';
import BaseModal from '@components/common/form/BaseModal';
import ListPage from '@components/common/layout/ListPage';
import PageWrapper from '@components/common/layout/PageWrapper';
import {
    AppConstants,
    DATE_FORMAT_DISPLAY2,
    DEFAULT_TABLE_ITEM_SIZE_LARGE,
    EMPTY_DETAIL,
    STATUS_ACTIVE,
    UserTypes,
} from '@constants';
import apiConfig from '@constants/apiConfig';
import { FieldTypes } from '@constants/formConfig';
import { statusOptions } from '@constants/masterData';
import useAuth from '@hooks/useAuth';
import useListModal from '@hooks/useListModal';
import useTranslate from '@hooks/useTranslate';
import { AvatarField, BaseTable, BaseTooltip } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import { convertUtcToLocalTime } from '@utils';
import { Button, Flex } from 'antd';

import UserAdminForm from './UserAdminForm';

const FORM_ID = 'form-user-admin';

const UserAdminListPage = ({ pageOptions }) => {
    const translate = useTranslate();
    const { isAdmin } = useAuth();
    const statusValues = translate.formatKeys(statusOptions, ['label']);

    const {
        data,
        mixinFuncs,
        queryFilter,
        loading,
        pagination,
        detail,
        loadingSave,
        openModal,
        onCancelModal,
        setIsChangedFormValues,
        onSave,
        isEditing,
        isChanged,
        titleModal,
    } = useListModal({
        apiConfig: {
            getList: apiConfig.account.getList,
            getById: apiConfig.account.getById,
            create: apiConfig.account.createAdmin,
            update: apiConfig.account.updateAdmin,
            delete: apiConfig.account.delete,
            changeStatus: apiConfig.account.updateAdmin,
        },
        options: {
            pageSize: DEFAULT_TABLE_ITEM_SIZE_LARGE,
            objectName: translate.formatMessage(pageOptions.objectName),
        },
        override: (funcs) => {
            funcs.mappingData = (response) => {
                if (response.result === true) {
                    return {
                        data: response.data.content,
                        total: response.data.totalElements,
                    };
                }
            };
            funcs.getList = () => {
                const params = {
                    ...queryFilter,
                    page: pagination.current - 1,
                    size: pagination.pageSize,
                    kind: UserTypes.ADMIN,
                };
                return mixinFuncs.handleFetchList(params);
            };
            funcs.additionalActionColumnButtons = () => ({
                delete: (record) => {
                    return (
                        !record?.isSuperAdmin && (
                            <BaseTooltip type="delete">
                                <Button
                                    type="link"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        mixinFuncs.showDeleteItemConfirm(record.id);
                                    }}
                                    style={{ padding: 0 }}
                                >
                                    <DeleteOutlined style={{ color: 'red' }} />
                                </Button>
                            </BaseTooltip>
                        )
                    );
                },
            });
        },
    });

    const handleSubmitForm = (values) => {
        const preparedData = isEditing
            ? {
                id: detail?.id,
                fullName: values.fullName,
                email: values.email,
                username: values.username,
                groupId: values.groupId,
                status: values.status,
                kind: UserTypes.ADMIN,
                ...(values.password ? { password: values.password } : {}),
            }
            : {
                ...values,
                kind: UserTypes.ADMIN,
            };
        onSave(preparedData);
    };

    const columns = [
        {
            title: 'Avatar',
            dataIndex: 'avatarPath',
            align: 'center',
            width: 100,
            render: (avatar) => (
                <AvatarField
                    size="large"
                    icon={<UserOutlined />}
                    src={avatar ? `${AppConstants.contentRootUrl}${avatar}` : null}
                />
            ),
        },
        { title: translate.formatMessage(commonMessage.username), dataIndex: 'username' },
        { title: translate.formatMessage(commonMessage.fullName), dataIndex: 'fullName' },

        isAdmin && { title: translate.formatMessage(commonMessage.email), dataIndex: 'email', width: '200px' },
        // {
        //     title: translate.formatMessage(commonMessage.createdDate),
        //     dataIndex: 'createdDate',
        //     render: (expireDate) => {
        //         const expireDateLocal = convertUtcToLocalTime(expireDate, DATE_FORMAT_DISPLAY2, DATE_FORMAT_DISPLAY2);
        //         return <div>{expireDateLocal}</div>;
        //     },
        // },
        mixinFuncs.renderStatusColumn({ options: statusValues, width: '120px' }),
        mixinFuncs.renderActionColumn({ edit: true, delete: true }, { width: '120px' }),
    ].filter(Boolean);

    const searchFields = [
        {
            key: 'username',
            placeholder: translate.formatMessage(commonMessage.username),
        },
        {
            key: 'fullName',
            placeholder: translate.formatMessage(commonMessage.fullName),
        },
        {
            key: 'status',
            placeholder: translate.formatMessage(commonMessage.statusSelect),
            type: FieldTypes.SELECT,
            options: statusValues,
        },
    ];

    return (
        <PageWrapper routes={pageOptions.renderBreadcrumbs(commonMessage, translate)}>
            <ListPage
                searchForm={mixinFuncs.renderSearchForm({ fields: searchFields, initialValues: queryFilter })}
                actionBar={mixinFuncs.renderActionBar()}
                baseTable={
                    <BaseTable
                        onChange={mixinFuncs.changePagination}
                        columns={columns}
                        dataSource={data}
                        loading={loading}
                        rowKey={(record) => record.id}
                        pagination={pagination}
                    />
                }
            />
            {openModal && (
                <BaseModal
                    isModalVisible={openModal}
                    isEditing={isEditing}
                    handleCancel={onCancelModal}
                    title={titleModal}
                    width="800px"
                    noBodyPadding={true}
                    customFooter={
                        <Flex justify="end" gap={8} style={{ padding: '8px 16px' }}>
                            <Button danger onClick={onCancelModal} icon={<StopOutlined />}>
                                {translate.formatMessage(commonMessage.cancel)}
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                form={FORM_ID}
                                loading={loadingSave}
                                disabled={isEditing && !isChanged}
                                icon={<SaveOutlined />}
                            >
                                {translate.formatMessage(isEditing ? commonMessage.update : commonMessage.save)}
                            </Button>
                        </Flex>
                    }
                >
                    <UserAdminForm
                        formId={FORM_ID}
                        dataDetail={detail || EMPTY_DETAIL}
                        isEditing={isEditing}
                        actions={null}
                        onSubmit={handleSubmitForm}
                        setIsChangedFormValues={setIsChangedFormValues}
                    />
                </BaseModal>
            )}
        </PageWrapper>
    );
};

export default UserAdminListPage;
